import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams, useLocation, Link } from 'react-router';
import { useAuth } from '../lib/auth-context';
import { MfaChallengePanel } from '../components/MfaChallengePanel';
import { useAppSettings } from '../lib/app-settings-context';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { AlertCircle, Wrench, Gift, Users, Eye, EyeOff, ArrowLeft, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Card } from '../components/ui/card';
import { cn } from '../components/ui/utils';
import { TensionLogo } from '../components/TensionLogo';
import { isOnboardingComplete, markOnboardingComplete } from '../lib/onboarding-state';

export default function Auth() {
  const [searchParams] = useSearchParams();
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [birthYear, setBirthYear] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const {
    user,
    signUp,
    signIn,
    mfaChallenge,
    completeMfaTotp,
    completeMfaPasskey,
    cancelMfaChallenge,
    syncMfaFromSession,
  } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const returnTo = (location.state as { from?: string } | null)?.from;

  const { maintenanceMode } = useAppSettings();

  if (maintenanceMode) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-neutral-900 p-4">
        <Card className="max-w-md w-full p-8 text-center bg-neutral-800 border-red-700">
          <div className="bg-red-900/20 w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6 border-2 border-red-700">
            <Wrench className="w-10 h-10 text-red-400" />
          </div>
          <h1 className="text-2xl font-bold text-white mb-3">
            Down for Maintenance
          </h1>
          <p className="text-neutral-300 mb-4">
            TENSION is currently undergoing scheduled maintenance. We'll be back soon!
          </p>
          <p className="text-sm text-neutral-500">
            Thank you for your patience.
          </p>
        </Card>
      </div>
    );
  }

  // Referral: read ref param and persist for attribution after sign-up
  const refCode = searchParams.get('ref');
  const referrerLabel = (() => {
    if (!refCode || !refCode.trim()) return '';
    const part = refCode.trim().split('_')[0];
    return part && /^[a-zA-Z]+$/.test(part)
      ? part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
      : 'A friend';
  })();

  // Check URL params to determine if user should see sign up form
  useEffect(() => {
    const mode = searchParams.get('mode');
    const signup = searchParams.get('signup');
    const ref = searchParams.get('ref');
    const startTrial = searchParams.get('startTrial') === '1';
    const trialPlan = searchParams.get('trialPlan');
    const trialBillingRaw = searchParams.get('trialBilling');
    const trialBilling = trialBillingRaw === 'annual' ? 'annual' : 'monthly';
    const validTrialPlan = trialPlan === 'premium' || trialPlan === 'pro' ? trialPlan : null;

    if (ref && ref.trim()) {
      sessionStorage.setItem('tension_referral_ref', ref.trim());
    }
    if (startTrial && validTrialPlan) {
      sessionStorage.setItem(
        'tension_trial_intent',
        JSON.stringify({ plan: validTrialPlan, billing: trialBilling }),
      );
      setIsSignUp(true);
    }

    if (mode === 'signup' || signup === 'true' || (ref && ref.trim())) {
      setIsSignUp(true);
    }
  }, [searchParams]);

  const hydrateProfileFromMetadata = useCallback(() => {
    const metadata = user?.user_metadata;
    if (!metadata) return;

    const parseMaybeJson = (value: unknown): Record<string, unknown> | null => {
      if (value && typeof value === 'object') return value as Record<string, unknown>;
      if (typeof value === 'string') {
        try {
          const parsed = JSON.parse(value);
          return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
        } catch {
          return null;
        }
      }
      return null;
    };

    const relationshipProfile = parseMaybeJson(metadata.tension_relationship_profile);
    const userProfile = parseMaybeJson(metadata.tension_user_profile);
    const partnerName =
      typeof relationshipProfile?.partnerName === 'string'
        ? relationshipProfile.partnerName
        : metadata.tension_partner_name;
    const relationshipType =
      typeof relationshipProfile?.relationshipType === 'string'
        ? relationshipProfile.relationshipType
        : metadata.tension_relationship_type;
    const relationshipStart =
      typeof relationshipProfile?.relationshipStartDate === 'string'
        ? relationshipProfile.relationshipStartDate
        : metadata.tension_relationship_start_date;
    const onboardingComplete = metadata.tension_onboarding_complete;
    const mode = metadata.tension_mode;

    if (onboardingComplete === 'true' && !isOnboardingComplete()) {
      markOnboardingComplete();
    }
    if (mode === 'relationship' || mode === 'single') {
      if (!localStorage.getItem('tension_mode')) {
        localStorage.setItem('tension_mode', mode);
      }
    }

    const hasRelationships = (() => {
      try {
        const raw = localStorage.getItem('tension_relationships');
        const parsed = raw ? JSON.parse(raw) : [];
        return Array.isArray(parsed) && parsed.length > 0;
      } catch {
        return false;
      }
    })();

    if (!hasRelationships && partnerName && relationshipType) {
      const relationshipId = `rel_${Date.now()}`;
      const relationship = {
        id: relationshipId,
        name: partnerName,
        type: relationshipType,
        startDate: relationshipStart || new Date().toISOString().split('T')[0],
        createdAt: new Date().toISOString(),
        isActive: true,
      };
      localStorage.setItem('tension_relationships', JSON.stringify([relationship]));
      localStorage.setItem('tension_active_relationship', relationshipId);
      markOnboardingComplete();
      localStorage.setItem('tension_mode', 'relationship');
    }

    if (userProfile && !localStorage.getItem('tension_user_profile')) {
      localStorage.setItem('tension_user_profile', JSON.stringify(userProfile));
    }
    if (relationshipProfile && !localStorage.getItem('tension_relationship_profile')) {
      localStorage.setItem('tension_relationship_profile', JSON.stringify(relationshipProfile));
    }
  }, [user]);

  const navigateAfterSignIn = useCallback(() => {
    hydrateProfileFromMetadata();
    const authFlow = sessionStorage.getItem('tension_auth_flow');
    sessionStorage.removeItem('tension_auth_flow');
    const trialIntentRaw = sessionStorage.getItem('tension_trial_intent');
    if (trialIntentRaw) {
      sessionStorage.removeItem('tension_trial_intent');
      try {
        const parsed = JSON.parse(trialIntentRaw) as { plan?: string; billing?: string };
        const plan = parsed.plan === 'premium' || parsed.plan === 'pro' ? parsed.plan : null;
        const billing = parsed.billing === 'annual' ? 'annual' : 'monthly';
        if (plan) {
          navigate(`/pricing?startTrial=1&trialPlan=${encodeURIComponent(plan)}&trialBilling=${encodeURIComponent(billing)}`, { replace: true });
          return;
        }
      } catch {
        // Ignore malformed trial intent payload and continue normal auth routing.
      }
    }

    // Redirect to admin if that's where they came from
    if (returnTo && (returnTo === '/admin' || returnTo.startsWith('/admin/'))) {
      navigate(returnTo, { replace: true });
      return;
    }

    // Returning sign-ins should land in app shell so cloud recovery can hydrate data.
    if (authFlow === 'signin') {
      navigate('/home', { replace: true });
      return;
    }

    const existingMode = localStorage.getItem('tension_mode');
    const hasCompletedOnboarding = isOnboardingComplete();

    if (hasCompletedOnboarding) {
      navigate('/home', { replace: true });
    } else if (existingMode) {
      navigate(existingMode === 'single' ? '/singles-onboarding' : '/onboarding', { replace: true });
    } else {
      navigate('/mode-selection', { replace: true });
    }
  }, [navigate, returnTo, hydrateProfileFromMetadata]);

  useEffect(() => {
    if (searchParams.get('mfa') !== '1') return;
    let cancelled = false;
    (async () => {
      const pending = await syncMfaFromSession();
      if (cancelled) return;
      if (!pending) {
        const { supabase } = await import('../lib/supabase');
        const {
          data: { session },
        } = await supabase.auth.getSession();
        if (session?.user) {
          navigateAfterSignIn();
        } else {
          navigate('/auth', { replace: true });
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [searchParams, syncMfaFromSession, navigateAfterSignIn, navigate]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    // Must exceed worst-case auth path: fetch (20s) + setSession (15s) + MFA checks (16s)
    const safetyTimeout = setTimeout(() => {
      setLoading((prev) => {
        if (prev) {
          toast.error('Request timed out. Please check your connection and try again.');
          return false;
        }
        return prev;
      });
    }, 55_000);

    try {
      if (isSignUp) {
        sessionStorage.setItem('tension_auth_flow', 'signup');

        const parsedBirthYear = parseInt(birthYear, 10);
        const currentYear = new Date().getFullYear();
        const age = currentYear - parsedBirthYear;
        if (!parsedBirthYear || parsedBirthYear < 1900 || parsedBirthYear > currentYear) {
          toast.error('Please enter a valid birth year.');
          setLoading(false);
          return;
        }
        if (age < 18) {
          toast.error('You must be 18 or older to create an account.');
          setLoading(false);
          return;
        }

        const { mfaPending } = await signUp(email, password, name);
        if (mfaPending) {
          toast.info('Enter your two-factor code to finish signing in');
          setLoading(false);
          return;
        }

        // Persist referral attribution (came from ?ref= link)
        const referredBy = sessionStorage.getItem('tension_referral_ref');
        if (referredBy) {
          localStorage.setItem('tension_referred_by', referredBy);
          sessionStorage.removeItem('tension_referral_ref');
        }

        localStorage.setItem('tension_age_verified', 'true');
        localStorage.setItem('tension_age_verified_date', new Date().toISOString());
        toast.success('Welcome to TENSION!', { duration: 3000 });

        navigateAfterSignIn();
        
      } else {
        sessionStorage.setItem('tension_auth_flow', 'signin');
        const { mfaPending } = await signIn(email, password);
        if (mfaPending) {
          toast.info('Enter your two-factor code to finish signing in');
          setLoading(false);
          return;
        }
        toast.success('Signed in successfully');
        navigateAfterSignIn();
      }
    } catch (error: any) {
      console.error('❌ Auth error:', error);
      
      // ✅ FIX: Better error handling with specific messages
      if (error.message?.includes('USER_ALREADY_EXISTS') || error.message?.includes('already exists')) {
        toast.error('This email is already registered', {
          description: 'Please sign in instead or use a different email.',
          duration: 5000,
        });
        // Auto-switch to sign in mode
        setTimeout(() => setIsSignUp(false), 2000);
      } else if (error.message?.includes('INVALID_CREDENTIALS') || error.message?.includes('Invalid login')) {
        toast.error('Invalid email or password', {
          description: 'Please check your credentials and try again.',
          duration: 5000,
        });
      } else {
        toast.error(error.message || 'Authentication failed', {
          description: 'Please try again or contact support if the issue persists.',
          duration: 5000,
        });
      }
    } finally {
      clearTimeout(safetyTimeout);
      setLoading(false);
    }
  };

  const fieldClass =
    'auth-readable-input min-h-11 w-full !border-slate-300 !bg-slate-50 !text-slate-900 placeholder:!text-slate-500 md:text-sm shadow-[inset_0_1px_0_rgba(255,255,255,0.9)]';

  return (
    <div className="flex min-h-[100dvh] flex-col tension-page-bg">
      <header className="android-safe-pt shrink-0 border-b border-slate-200 bg-white/98 shadow-[0_10px_24px_rgba(15,23,42,0.08)] backdrop-blur-[6px]">
        <div className="mx-auto flex min-h-14 max-w-6xl items-center justify-between gap-3 px-4 py-2">
          <button
            type="button"
            onClick={() => navigate('/')}
            className="inline-flex min-h-[44px] min-w-[44px] items-center justify-center gap-2 rounded-xl px-2 text-sm font-medium text-neutral-700 transition-colors hover:bg-neutral-100 hover:text-neutral-900 dark:text-neutral-200 dark:hover:bg-white/10 dark:hover:text-white sm:justify-start sm:px-3"
          >
            <ArrowLeft className="h-4 w-4 shrink-0" aria-hidden />
            <span className="hidden sm:inline">Back</span>
          </button>
          <Link
            to="/"
            className="flex min-h-[44px] items-center gap-2 rounded-xl px-2 transition-opacity hover:opacity-90"
          >
            <TensionLogo tone="dark" height={50} priority className="h-auto max-w-[230px] sm:max-w-[280px]" />
          </Link>
          <div aria-hidden />
        </div>
      </header>

      <div className="flex min-h-0 flex-1 flex-col lg:flex-row lg:items-stretch lg:justify-center">
        {/* Desktop story column */}
        <div className="relative hidden overflow-hidden lg:flex lg:w-[44%] lg:max-w-xl lg:flex-col lg:justify-start lg:border-r lg:border-white/10 lg:px-10 lg:pt-10 xl:px-14">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(ellipse_at_30%_0%,rgba(168,85,247,0.22),transparent_50%),radial-gradient(ellipse_at_100%_80%,rgba(236,72,153,0.12),transparent_45%)]" />
          <div className="relative space-y-6">
            <p className="text-xs font-semibold uppercase tracking-[0.2em] text-purple-700 dark:text-purple-300/95">
              Relationship intelligence
            </p>
            <h1 className="text-4xl font-bold leading-[1.1] tracking-tight text-slate-900 dark:text-white xl:text-[2.75rem]">
              {isSignUp ? 'Start your journey' : 'Welcome back'}
            </h1>
            <p className="text-lg leading-relaxed text-slate-700 dark:text-neutral-300">
              {isSignUp
                ? 'Daily check-ins, tension scoring (RTI), and actions that reduce escalation—built for real couples.'
                : 'Pick up where you left off. Your data stays on your device by default.'}
            </p>
            <ul className="space-y-3 text-slate-700 dark:text-neutral-200">
              {[
                '5-component tension score (RTI) & trends',
                '2-minute check-ins that fit your day',
                'Privacy-first: local by default',
              ].map((line) => (
                <li key={line} className="flex items-start gap-2.5 text-sm leading-snug">
                  <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-fuchsia-400" aria-hidden />
                  {line}
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Form column */}
        <div className="flex flex-1 flex-col justify-start px-4 pt-4 pb-[max(1.25rem,env(safe-area-inset-bottom))] sm:px-6 sm:pt-6 lg:max-w-lg lg:flex-none lg:overflow-y-auto lg:px-10 lg:pt-10 lg:pb-10 xl:max-w-xl">
          <div className="mx-auto w-full max-w-md space-y-5">
            <div className="text-center lg:hidden">
              <p className="text-sm text-neutral-700 dark:text-neutral-200">Measure what words can&apos;t</p>
            </div>

            {import.meta.env.DEV && (
              <button
                type="button"
                onClick={() => {
                  localStorage.clear();
                  toast.success('Session cleared. Reloading…');
                  window.location.reload();
                }}
                className="w-full rounded-lg border border-red-500/40 bg-red-950/30 py-2 text-xs text-red-300 hover:bg-red-950/50"
              >
                [DEV] Clear storage & reload
              </button>
            )}

            {refCode && referrerLabel && (
              <Card className="border border-violet-400/30 bg-gradient-to-br from-violet-50 to-pink-50 p-4 shadow-md sm:p-5">
                <div className="flex items-start gap-3">
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-violet-500 to-pink-500">
                    <Gift className="h-5 w-5 text-white" />
                  </div>
                  <div className="min-w-0 space-y-2">
                    <p className="font-semibold text-neutral-900">{referrerLabel} invited you</p>
                    <p className="text-sm text-neutral-700">
                      Sign up for check-ins and score insights—and help {referrerLabel} earn rewards.
                    </p>
                    <div className="flex items-start gap-2 pt-0.5">
                      <Users className="mt-0.5 h-4 w-4 shrink-0 text-violet-600" />
                      <p className="text-xs text-violet-800">
                        <strong>1 month Pro free</strong> for every 3 friends who join.
                      </p>
                    </div>
                  </div>
                </div>
              </Card>
            )}

            <div className="grid grid-cols-2 gap-1 rounded-xl border border-white/15 bg-black/35 p-1 backdrop-blur-md">
              <button
                type="button"
                onClick={() => {
                  setIsSignUp(false);
                }}
                className={cn(
                  'min-h-[44px] rounded-lg text-sm font-semibold transition-all',
                  !isSignUp ? 'bg-white text-neutral-900 shadow-md' : 'text-neutral-800 hover:text-neutral-900 dark:text-neutral-300 dark:hover:text-neutral-100',
                )}
              >
                Sign in
              </button>
              <button
                type="button"
                onClick={() => {
                  setIsSignUp(true);
                }}
                className={cn(
                  'min-h-[44px] rounded-lg text-sm font-semibold transition-all',
                  isSignUp ? 'bg-white text-neutral-900 shadow-md' : 'text-neutral-800 hover:text-neutral-900 dark:text-neutral-300 dark:hover:text-neutral-100',
                )}
              >
                Create account
              </button>
            </div>

            <Card className="space-y-6 rounded-2xl border border-violet-200/80 bg-white/95 p-6 text-neutral-900 shadow-2xl shadow-violet-900/20 sm:p-8 dark:bg-white">
              {mfaChallenge ? (
                <MfaChallengePanel
                  challenge={mfaChallenge}
                  completeMfaTotp={completeMfaTotp}
                  completeMfaPasskey={completeMfaPasskey}
                  cancelMfaChallenge={cancelMfaChallenge}
                  onSuccess={() => {
                    toast.success('Signed in successfully');
                    navigateAfterSignIn();
                  }}
                />
              ) : null}

              <form
                onSubmit={handleSubmit}
                className={cn('space-y-4', mfaChallenge && 'hidden')}
              >
            {isSignUp && (
              <div className="space-y-2">
                <Label htmlFor="name" className="text-neutral-900">Name</Label>
                <Input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  autoComplete="name"
                  placeholder="Full name"
                  className={fieldClass}
                />
              </div>
            )}

            {isSignUp && (
              <div className="space-y-2">
                <Label htmlFor="birthYear" className="text-neutral-900">Year of Birth</Label>
                <Input
                  id="birthYear"
                  type="number"
                  value={birthYear}
                  onChange={(e) => setBirthYear(e.target.value)}
                  required
                  min={1900}
                  max={new Date().getFullYear()}
                  placeholder="YYYY"
                  className={fieldClass}
                />
                <p className="text-xs text-neutral-700 dark:text-neutral-300">You must be 18 or older to create an account.</p>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="email" className="text-neutral-900">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                inputMode="email"
                placeholder="Email address"
                className={fieldClass}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-neutral-900">
                Password
              </Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  className={cn(fieldClass, 'pr-12')}
                  minLength={6}
                  autoComplete={isSignUp ? 'new-password' : 'current-password'}
                  placeholder={isSignUp ? 'Create a password' : 'Enter your password'}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1.5 text-neutral-700 hover:bg-neutral-100 hover:text-neutral-900 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-purple-500/50"
                  aria-label={showPassword ? 'Hide password' : 'Show password'}
                  aria-pressed={showPassword}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" aria-hidden />
                  ) : (
                    <Eye className="h-4 w-4" aria-hidden />
                  )}
                </button>
              </div>
              {isSignUp && (
                <p className="text-xs text-neutral-700 dark:text-neutral-300">
                  Minimum 6 characters
                </p>
              )}
              {/* Real-time validation feedback */}
              {isSignUp && password && password.length > 0 && password.length < 6 && (
                <p className="text-xs text-red-600 flex items-center gap-1">
                  <span>⚠️</span> Password too short ({password.length}/6)
                </p>
              )}
              {isSignUp && password && password.length >= 6 && (
                <p className="text-xs text-green-600 flex items-center gap-1">
                  <span>✓</span> Password strength: Good
                </p>
              )}
            </div>

            <Button
              type="submit"
              className="min-h-12 w-full border-0 bg-gradient-to-r from-purple-600 to-pink-600 text-base font-semibold text-white hover:from-purple-700 hover:to-pink-700"
              disabled={loading}
            >
              {loading ? 'Please wait…' : isSignUp ? 'Create account' : 'Sign in'}
            </Button>

            {!isSignUp && (
              <div className="text-center">
                <button
                  type="button"
                  onClick={() => navigate('/password-reset')}
                  className="min-h-[44px] text-sm font-medium text-purple-700 underline-offset-2 hover:text-purple-900 hover:underline"
                >
                  Forgot password?
                </button>
              </div>
            )}
              </form>
            </Card>

            <p className="px-1 text-center text-xs leading-relaxed text-neutral-700 dark:text-neutral-300">
              TENSION is an informational tool for self-awareness—not therapy or medical advice.
            </p>
          </div>
        </div>
      </div>

    </div>
  );
}